import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Trash2, Phone, Dog, Upload, Plus, X, Wallet, CalendarClock, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AdminLayout } from "./AdminLayout";
import { ClientForm } from "./ClientForm";
import { clientsApi, processImage, eur, fmtDate, formatApiErrorDetail } from "./api";

const SERVICES = ["Banho", "Tosquia", "Banho + Tosquia", "Corte de unhas", "Grooming completo"];

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await clientsApi.get(id);
      setClient(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const refreshMeta = (data) => setClient((c) => ({ ...c, ...data, photos: c.photos }));

  const saveEdit = async (form) => {
    try {
      const res = await clientsApi.update(id, form);
      refreshMeta(res.data);
      setEditOpen(false);
      toast.success("Ficha atualizada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeClient = async () => {
    try {
      await clientsApi.remove(id);
      toast.success("Ficha eliminada");
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const { data, thumb } = await processImage(file);
        const res = await clientsApi.addPhoto(id, { data, thumb });
        setClient((c) => ({ ...c, photos: [res.data, ...(c.photos || [])], photos_count: (c.photos_count || 0) + 1 }));
      }
      toast.success(files.length > 1 ? "Fotos adicionadas" : "Foto adicionada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao carregar foto");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (pid) => {
    try {
      await clientsApi.removePhoto(id, pid);
      setClient((c) => ({ ...c, photos: c.photos.filter((p) => p.id !== pid), photos_count: c.photos_count - 1 }));
      setLightbox(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const addVisit = async (visit) => {
    try {
      const res = await clientsApi.addVisit(id, visit);
      refreshMeta(res.data);
      toast.success("Visita registada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeVisit = async (vid) => {
    try {
      const res = await clientsApi.removeVisit(id, vid);
      refreshMeta(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading || !client) {
    return (
      <AdminLayout>
        <div className="py-20 flex justify-center">
          <Loader2 className="animate-spin text-[#E20074]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <button onClick={() => navigate("/admin")} data-testid="back-btn" className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0A0A0A] mb-6">
        <ArrowLeft size={16} /> Voltar às fichas
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[#525252] text-sm">Ficha de cliente</p>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight" data-testid="detail-dog-name">
            {client.dog_name || "Sem nome"}
          </h1>
          <p className="text-[#525252] mt-1">
            {client.dog_breed || "Sem raça"}{client.dog_age ? ` · ${client.dog_age}` : ""} · Dono: <span className="text-[#0A0A0A] font-medium">{client.owner_name}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-client-btn">
              <Pencil size={15} className="mr-1.5" /> Editar
            </Button>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Editar ficha</DialogTitle><DialogDescription>Atualize os dados do cliente e do cão.</DialogDescription></DialogHeader>
              <ClientForm initial={client} onSubmit={saveEdit} onCancel={() => setEditOpen(false)} />
            </DialogContent>
          </Dialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-[#E20074] border-[#E20074]/40 hover:bg-[#E20074] hover:text-white" data-testid="delete-client-btn">
                <Trash2 size={15} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminar ficha?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação elimina permanentemente a ficha de {client.owner_name}, incluindo fotos e histórico de visitas.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={removeClient} className="bg-[#E20074] hover:bg-[#c00063]" data-testid="confirm-delete">
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Kpi icon={Wallet} label="Total gasto" value={eur(client.total_spent)} accent testid="kpi-total" />
        <Kpi icon={CalendarClock} label="Última visita" value={fmtDate(client.last_visit)} testid="kpi-last" />
        <Kpi icon={Dog} label="Visitas" value={client.visits_count} testid="kpi-visits" />
        <Kpi icon={Phone} label="Telefone" value={client.phone || "—"} testid="kpi-phone" />
      </div>

      {client.notes && (
        <div className="bg-white border border-[#D4D4D4] rounded-xl p-5 mb-8">
          <p className="text-xs uppercase tracking-wide text-[#525252] mb-1">Notas</p>
          <p className="text-[#0A0A0A] whitespace-pre-wrap" data-testid="detail-notes">{client.notes}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Photos */}
        <section className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-2xl">Fotos do cão</h2>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="photo-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#00B4D8] hover:bg-[#0098b8] text-[#003] rounded-full" data-testid="upload-photo-btn">
              {uploading ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Upload size={16} className="mr-1.5" />}
              {uploading ? "A carregar…" : "Adicionar fotos"}
            </Button>
          </div>
          {(!client.photos || client.photos.length === 0) ? (
            <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-[#D4D4D4] rounded-xl py-16 text-center text-[#525252] hover:border-[#00B4D8] transition-colors" data-testid="photos-empty">
              <Dog size={36} className="mx-auto mb-2 text-[#D4D4D4]" />
              Sem fotos ainda. Clique para adicionar.
            </button>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photos-grid">
              {client.photos.map((p) => (
                <motion.div key={p.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="relative group aspect-square rounded-lg overflow-hidden border border-[#D4D4D4]">
                  <img src={p.data} alt="cão" className="w-full h-full object-cover cursor-pointer" onClick={() => setLightbox(p)} />
                  <button onClick={() => removePhoto(p.id)} data-testid={`delete-photo-${p.id}`} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#E20074]">
                    <Trash2 size={15} />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* Visits */}
        <section className="lg:col-span-2">
          <h2 className="font-display font-bold text-2xl mb-4">Histórico de visitas</h2>
          <VisitForm onAdd={addVisit} />
          <div className="mt-5 space-y-3" data-testid="visits-list">
            {client.visits.length === 0 ? (
              <p className="text-[#525252] text-sm">Sem visitas registadas.</p>
            ) : (
              client.visits.map((v) => (
                <div key={v.id} className="bg-white border border-[#D4D4D4] rounded-lg p-4 flex items-start justify-between gap-3" data-testid={`visit-${v.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{v.service}</span>
                      <span className="text-[#E20074] font-display font-bold">{eur(v.price)}</span>
                    </div>
                    <p className="text-xs text-[#525252] mt-0.5">{fmtDate(v.date)}</p>
                    {v.notes && <p className="text-sm text-[#525252] mt-1">{v.notes}</p>}
                  </div>
                  <button onClick={() => removeVisit(v.id)} data-testid={`delete-visit-${v.id}`} className="text-[#525252] hover:text-[#E20074]">
                    <X size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-2xl p-2">
          {lightbox && <img src={lightbox.data} alt="cão" className="w-full h-auto rounded-md" />}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl p-4 border ${accent ? "bg-[#E20074] border-[#E20074] text-white" : "bg-white border-[#D4D4D4]"}`}>
      <Icon size={18} className={accent ? "text-white/80" : "text-[#525252]"} />
      <p className={`text-xs uppercase tracking-wide mt-2 ${accent ? "text-white/80" : "text-[#525252]"}`}>{label}</p>
      <p className="font-display font-black text-xl mt-0.5 truncate">{value}</p>
    </div>
  );
}

function VisitForm({ onAdd }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [service, setService] = useState(SERVICES[2]);
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onAdd({ date, service, price: parseFloat(price) || 0, notes });
    setSaving(false);
    setPrice("");
    setNotes("");
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#D4D4D4] rounded-xl p-4 space-y-3" data-testid="visit-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="v-date" className="text-xs">Data</Label>
          <Input id="v-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required data-testid="visit-date" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="v-price" className="text-xs">Valor (€)</Label>
          <Input id="v-price" type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" data-testid="visit-price" />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="v-service" className="text-xs">Serviço</Label>
        <Input id="v-service" list="services-list" value={service} onChange={(e) => setService(e.target.value)} required data-testid="visit-service" />
        <datalist id="services-list">
          {SERVICES.map((s) => <option key={s} value={s} />)}
        </datalist>
      </div>
      <div className="space-y-1">
        <Label htmlFor="v-notes" className="text-xs">Notas</Label>
        <Textarea id="v-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="visit-notes" />
      </div>
      <Button type="submit" disabled={saving} className="w-full bg-[#0A0A0A] hover:bg-[#E20074] text-white" data-testid="add-visit-btn">
        <Plus size={16} className="mr-1" /> {saving ? "A registar…" : "Registar visita"}
      </Button>
    </form>
  );
}
